import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  DEFAULT_WORKFLOW_CHART_TYPE,
  getInsightDashboardSeriesLimits,
  getIsWorkflowChartDimension,
  getIsWorkflowChartMeasure,
  INSIGHT_CHART_CATEGORY_LIMIT,
  WORKFLOW_CHART_DIMENSION_LABELS,
  WORKFLOW_CHART_DIMENSION_PLURAL_LABELS,
  WORKFLOW_CHART_HIDDEN_SERIES_KEY,
  WORKFLOW_CHART_MEASURES,
  WorkflowChartSeriesValueSchema,
} from '@aline/common';
import type { WorkflowChartDimension, WorkflowChartType } from '@aline/common';
import { link, params } from '@aline/routes';
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  ListItemButton,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { rainbowSurgePalette } from '@mui/x-charts-premium/colorPalettes';
import { useChartPremiumApiRef } from '@mui/x-charts-premium/hooks';
import clsx from 'clsx';
import { Check, ImageDown, MoreHorizontal, Pencil, Search, Trash2 } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { AlineAvatar } from 'src/components/AlineAvatar';
import { AlineCheckbox } from 'src/components/generic/AlineCheckbox';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';
import { useDeepCompareMemo } from 'use-deep-compare';

import {
  fillInsightChartExportIframe,
  getCanExportInsightChart,
  getHasChartImageExport,
  getInsightChartExportFileName,
} from './insight-chart-export';
import {
  getSeriesColor,
  getShouldRotateCategoryLabels,
  INSIGHT_CHART_HEIGHT_PX,
  INSIGHT_CHART_PLOTS,
} from './insight-chart-plots';
import type { InsightChartApiRef } from './insight-chart-plots';
import { getChartModelFromSeries, getIsMedianSuppressed } from './insight-chart-series';
import type {
  ChartSeriesResult,
  InsightChartCategory,
  InsightChartModel,
} from './insight-chart-series';
import { getInsightTooltipSeriesLabel, InsightChartTooltipProvider } from './InsightChartTooltip';
import {
  getDrilldownBucketsFromChartPoint,
  serializeWorkflowDrilldownPayload,
} from './workflow-drilldown';
import type { InsightChartDataPoint, WorkflowDrilldownPayload } from './workflow-drilldown';

const INSIGHT_CHART_UNQUERYABLE_SERIES_INPUT = {
  dimensions: [],
  measure: WORKFLOW_CHART_MEASURES.count,
} satisfies Apollo.WorkflowChartSeriesInput;
const useStyles = makeStyles<void, 'hoverAction'>()((theme, _params, classes) => ({
  card: {
    [`&:hover .${classes.hoverAction}, &:focus-within .${classes.hoverAction}`]: {
      opacity: 1,
    },
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 340,
    padding: theme.spacing(2),
  },
  cardWide: {
    '@container (max-width: 760px)': {
      gridColumn: 'span 1',
    },
    gridColumn: 'span 2',
  },
  chartSkeleton: {
    marginTop: theme.spacing(1),
  },
  controls: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    /**
     * Cards in a row stretch to the tallest one, so the overflow footer is pushed to
     * the bottom edge instead of floating under a short plot.
     */
    marginTop: 'auto',
    paddingTop: theme.spacing(0.5),
  },
  empty: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    minHeight: INSIGHT_CHART_HEIGHT_PX,
  },
  /** Inner contents of the card; this is what image export clones, so the PNG has no card border. */
  exportBody: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minHeight: 0,
    minWidth: 0,
  },
  footerAction: {
    fontSize: theme.typography.caption.fontSize,
  },
  footerRow: {
    alignItems: 'baseline',
    color: theme.palette.text.secondary,
    display: 'flex',
    flexWrap: 'wrap',
    fontSize: theme.typography.caption.fontSize,
    gap: theme.spacing(0.75),
    lineHeight: theme.typography.caption.lineHeight,
  },
  /** Revealed by the card's hover and focus-within rules. */
  hoverAction: {
    opacity: 0,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  hoverActionVisible: {
    opacity: 1,
  },
  interactiveChart: {
    cursor: 'pointer',
  },
  legend: {
    display: 'grid',
    gap: theme.spacing(0.5, 1.5),
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    marginTop: theme.spacing(0.5),
  },
  legendItem: {
    alignItems: 'center',
    display: 'flex',
    minWidth: 0,
  },
  legendLabel: {
    fontSize: theme.typography.caption.fontSize,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  legendSwatch: {
    borderRadius: 2,
    flex: '0 0 auto',
    height: 10,
    marginRight: theme.spacing(0.75),
    width: 10,
  },
  menuIcon: {
    flexShrink: 0,
    marginRight: theme.spacing(1),
  },
  selector: {
    display: 'flex',
    flexDirection: 'column',
    width: 360,
  },
  selectorEmpty: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(4, 2),
    textAlign: 'center',
  },
  selectorFooter: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
  },
  selectorHeader: {
    padding: theme.spacing(2, 2, 1.5),
  },
  selectorOption: {
    borderRadius: 8,
    gap: theme.spacing(1),
    minHeight: 34,
    padding: theme.spacing(0.25, 1),
  },
  selectorOptionCheck: {
    flex: '0 0 auto',
    padding: 0,
  },
  selectorOptionCount: {
    color: theme.palette.text.secondary,
    flex: '0 0 auto',
    fontSize: theme.typography.caption.fontSize,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  selectorOptionLabel: {
    flex: 1,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 500,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  selectorOptions: {
    maxHeight: 320,
    overflowY: 'auto',
    padding: theme.spacing(0.5),
  },
  selectorPaper: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    boxShadow: theme.appShadows.popper,
    overflow: 'hidden',
  },
  selectorSearch: {
    marginTop: theme.spacing(1.5),
  },
  systemChip: {
    border: theme.appBorders.outline,
    borderRadius: 6,
    color: theme.palette.text.secondary,
    cursor: 'default',
    fontSize: theme.typography.pxToRem(10),
    fontWeight: 500,
    padding: theme.spacing(0, 0.75),
    whiteSpace: 'nowrap',
  },
  title: {
    fontFamily: theme.typography.subtitle1.fontFamily,
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: 600,
    letterSpacing: theme.typography.subtitle1.letterSpacing,
    lineHeight: theme.typography.subtitle1.lineHeight,
  },
  titleActions: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(0.5),
  },
  titleRow: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
}));

type InsightChartPublisher =
  | { type: 'system' }
  | {
      type: 'user';
      user: Pick<Apollo.User, '__typename' | 'avatarColor' | 'displayName' | 'id'>;
    };

export interface InsightChartCardProps {
  /** `null` while a Chart has no Dimensions yet, which renders `emptyState` instead of a plot. */
  seriesInput: Apollo.WorkflowChartSeriesInput | null;
  title: string;
  /** Opens this Chart's full editor; dashboard cards intentionally expose no mutations. */
  actions?: {
    onEdit: () => void;
    editLabel?: string;
  };
  chartType?: WorkflowChartType;
  description?: string;
  drilldown?: Pick<WorkflowDrilldownPayload, 'presetId' | 'viewState'>;
  /** Fills the plot area when `seriesInput` is `null`, keeping the card's usual size. */
  emptyState?: React.ReactNode;
  headerActions?: React.ReactNode;
  /** Publication attribution shown on Insights cards; omitted in the focused Chart builder. */
  publisher?: InsightChartPublisher;
  /**
   * Takes this Chart off the board it is rendered on. Stays visible while
   * `disabledReason` is set so viewers without the permission learn why.
   */
  removeAction?: {
    onRemove: () => void;
    disabledReason?: string;
    label?: string;
  };
  /** Preloaded by the Insights dashboard query; the builder omits this and queries interactively. */
  series?: ChartSeriesResult;
  titleEditor?: {
    onSave: (title: string) => void;
    disabled?: boolean;
    initiallyEditing?: boolean;
  };
  /** Featured dashboard Charts span two grid columns when the section has room. */
  wide?: boolean;
}

export function InsightChartCard({
  actions,
  chartType = DEFAULT_WORKFLOW_CHART_TYPE,
  description,
  drilldown,
  emptyState,
  headerActions,
  publisher,
  removeAction,
  series: providedSeries,
  seriesInput,
  title,
  titleEditor,
  wide = false,
}: InsightChartCardProps): JSX.Element {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const exportRootRef = useRef<HTMLDivElement>(null);
  const chartApiRef = useChartPremiumApiRef<'composition'>();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [primaryKeys, setPrimaryKeys] = useState<string[] | null>(null);
  const [secondaryKeys, setSecondaryKeys] = useState<string[] | null>(null);
  const dashboardLimits = getInsightDashboardSeriesLimits({
    chartType,
    dimensions: seriesInput?.dimensions ?? [],
  });
  const usingProvidedSeries =
    Boolean(providedSeries) && primaryKeys === null && secondaryKeys === null;
  /**
   * Inputs are reconstructed by the Insights page as its independent Pin, user,
   * and hidden-System-Chart queries resolve. Keep an equivalent input stable so
   * those page renders do not restart Apollo/MUI's Chart update path.
   */
  const queryInput = useDeepCompareMemo(
    () => ({
      ...(seriesInput ?? INSIGHT_CHART_UNQUERYABLE_SERIES_INPUT),
      primaryKeys: primaryKeys ?? undefined,
      primaryLimit: dashboardLimits.primaryLimit,
      secondaryKeys: secondaryKeys ?? undefined,
      secondaryLimit: dashboardLimits.secondaryLimit,
    }),
    [dashboardLimits, primaryKeys, secondaryKeys, seriesInput]
  );
  const { data, error, loading, previousData } = Apollo.useGetWorkflowChartSeriesQuery({
    fetchPolicy: 'cache-and-network',
    skip: !seriesInput || usingProvidedSeries,
    variables: { input: queryInput },
  });

  /**
   * Choosing buckets hands the card off from the dashboard's preloaded series to its
   * own query, which has neither `data` nor `previousData` yet. Falling back to the
   * last series keeps the plot, legend and footer — and the open bucket picker —
   * mounted across that handoff instead of blanking to a skeleton.
   */
  const lastSeriesResultRef = useRef<ChartSeriesResult | null>(null);
  const seriesResult =
    (usingProvidedSeries ? providedSeries : undefined) ??
    data?.workflowChartSeries ??
    previousData?.workflowChartSeries ??
    lastSeriesResultRef.current ??
    undefined;

  useEffect(() => {
    if (seriesResult) lastSeriesResultRef.current = seriesResult;
  }, [seriesResult]);

  const measure = getIsWorkflowChartMeasure(queryInput.measure) ? queryInput.measure : undefined;
  /**
   * MUI Charts treats new model arrays as new series and animates them again.
   * Preserve the model while the actual series response is unchanged so unrelated
   * Insights-page query completions cannot make every loaded Chart repaint.
   */
  const overlayValues = Boolean(seriesInput?.values);
  const model = useMemo(
    () => getChartModelFromSeries(seriesResult, measure, overlayValues),
    [measure, overlayValues, seriesResult]
  );
  const hasRenderedModelRef = useRef(false);
  const skipAnimation = hasRenderedModelRef.current;

  useEffect(() => {
    if (model) hasRenderedModelRef.current = true;
  }, [model]);

  const colorDimension = queryInput.dimensions.at(1) ?? queryInput.dimensions.at(0);
  const handleSelectDataPoint = useCallback(
    (point: InsightChartDataPoint) => {
      if (!drilldown || !model) return;
      const buckets = getDrilldownBucketsFromChartPoint({
        dimensions: queryInput.dimensions,
        model,
        point,
        valueDateDimensions: getValueDateDimensions(seriesInput?.values),
      });
      if (!buckets.length) return;

      const payload = serializeWorkflowDrilldownPayload({
        buckets,
        presetId: drilldown.presetId,
        viewState: drilldown.viewState,
      });
      openLink(
        `${link.WorkflowList()}?${params.workflowDrilldown}=${encodeURIComponent(payload)}`,
        {
          applyRouteToExistingTab: true,
          title: 'Workflows',
        }
      );
    },
    [drilldown, model, queryInput.dimensions, seriesInput]
  );
  const chartEmptyState = seriesInput ? undefined : emptyState;
  const chartError = !usingProvidedSeries && Boolean(error);
  const chartLoading = !usingProvidedSeries && loading;
  const canExport = !chartEmptyState && !chartError && getCanExportInsightChart(model);
  const menuOpen = Boolean(menuAnchorEl);
  const closeMenu = useCallback(() => setMenuAnchorEl(null), []);
  const handleExport = useCallback(() => {
    closeMenu();
    const api = chartApiRef.current;
    if (!getHasChartImageExport(api) || !canExport) {
      enqueueSnackbar('This chart is not ready to export.', { variant: 'error' });
      return;
    }
    const exportRoot = exportRootRef.current;
    api.exportAsImage({
      fileName: getInsightChartExportFileName(title),
      onBeforeExport: iframe => {
        if (!exportRoot) return;
        fillInsightChartExportIframe(iframe, exportRoot);
      },
      type: 'image/png',
    });
  }, [canExport, chartApiRef, closeMenu, enqueueSnackbar, title]);

  return (
    <Paper className={clsx(classes.card, wide && classes.cardWide)} elevation={0}>
      <div ref={exportRootRef} className={classes.exportBody}>
        <div className={classes.titleRow}>
          <InsightChartTitle editor={titleEditor} title={title} />
          <div data-hide-on-export className={classes.titleActions}>
            <InsightChartCardMenu
              canExport={canExport}
              editAction={actions}
              menuAnchorEl={menuAnchorEl}
              menuOpen={menuOpen}
              removeAction={removeAction}
              onClose={closeMenu}
              onExport={handleExport}
              onOpen={setMenuAnchorEl}
            />
            {publisher && <InsightChartPublisher publisher={publisher} />}
            {headerActions}
          </div>
        </div>
        {Boolean(description) && (
          <Typography color="textSecondary" variant="caption">
            {description}
          </Typography>
        )}
        <InsightChartBody
          apiRef={chartApiRef}
          chartType={chartType}
          colorDimension={colorDimension}
          emptyState={chartEmptyState}
          error={chartError}
          loading={chartLoading}
          model={model}
          primaryLabel={getDimensionAxisLabel(queryInput.dimensions.at(0))}
          rotateCategoryLabels={getShouldRotateCategoryLabels({
            dimension: queryInput.dimensions.at(0),
            labels: model?.xLabels,
          })}
          secondaryLabel={getDimensionLabel(queryInput.dimensions.at(1))}
          skipAnimation={skipAnimation}
          onSelectDataPoint={drilldown ? handleSelectDataPoint : undefined}
        />
        {model && <InsightChartLegend colorDimension={colorDimension} model={model} />}
        {model && (
          <InsightChartControls
            dimensions={queryInput.dimensions}
            model={model}
            primaryKeys={primaryKeys}
            secondaryKeys={secondaryKeys}
            onPrimaryKeysChange={setPrimaryKeys}
            onSecondaryKeysChange={setSecondaryKeys}
          />
        )}
      </div>
    </Paper>
  );
}

/** Loading stand-in that holds a Chart card's place, including a wide card's span. */
export function InsightChartCardSkeleton({ wide = false }: { wide?: boolean }): JSX.Element {
  const { classes } = useStyles();

  return (
    <Paper className={clsx(classes.card, wide && classes.cardWide)} elevation={0}>
      <div className={classes.exportBody}>
        <Skeleton height={24} variant="text" width={168} />
        <Skeleton
          className={classes.chartSkeleton}
          height={INSIGHT_CHART_HEIGHT_PX}
          variant="rounded"
        />
      </div>
    </Paper>
  );
}

function InsightChartTitle({
  editor,
  title,
}: {
  title: string;
  editor?: InsightChartCardProps['titleEditor'];
}): JSX.Element {
  const { classes } = useStyles();

  if (editor) {
    return (
      <InlineEditField
        classes={{ display: classes.title, input: classes.title }}
        disabled={editor.disabled}
        editTooltip="Edit chart title"
        iconSize={16}
        initiallyEditing={editor.initiallyEditing}
        placeholder="Add a chart title..."
        textPlaceholder="Untitled chart"
        value={title}
        onSave={editor.onSave}
      />
    );
  }

  return (
    <Typography className={classes.title} variant="subtitle1">
      {title}
    </Typography>
  );
}

function InsightChartPublisher({ publisher }: { publisher: InsightChartPublisher }): JSX.Element {
  const { classes } = useStyles();

  if (publisher.type === 'system') {
    return <Typography className={classes.systemChip}>System</Typography>;
  }

  return (
    <AlineTooltip arrow placement="top" title={`Published by ${publisher.user.displayName}`}>
      <AlineAvatar avatarInfo={publisher.user} styles={{ size: 'sm' }} />
    </AlineTooltip>
  );
}

function InsightChartCardMenu({
  canExport,
  editAction,
  menuAnchorEl,
  menuOpen,
  onClose,
  onExport,
  onOpen,
  removeAction,
}: {
  canExport: boolean;
  menuAnchorEl: HTMLElement | null;
  menuOpen: boolean;
  onClose: () => void;
  onExport: () => void;
  onOpen: (el: HTMLElement) => void;
  editAction?: InsightChartCardProps['actions'];
  removeAction?: InsightChartCardProps['removeAction'];
}): JSX.Element {
  const { classes } = useStyles();
  const editLabel = editAction?.editLabel ?? 'Edit';

  return (
    <>
      <AlineTooltip arrow placement="top" title="Chart actions">
        <IconButton
          aria-label="Chart actions"
          className={clsx(classes.hoverAction, menuOpen && classes.hoverActionVisible)}
          size="small"
          onClick={event => onOpen(event.currentTarget)}
        >
          <MoreHorizontal size={16} />
        </IconButton>
      </AlineTooltip>
      <Menu
        anchorEl={menuAnchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={menuOpen}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={onClose}
      >
        {editAction && (
          <MenuItem
            dense
            onClick={() => {
              onClose();
              editAction.onEdit();
            }}
          >
            <Pencil className={classes.menuIcon} size={16} />
            {editLabel}
          </MenuItem>
        )}
        {removeAction && <InsightChartMenuDeleteItem action={removeAction} onClose={onClose} />}
        <MenuItem dense disabled={!canExport} onClick={onExport}>
          <ImageDown className={classes.menuIcon} size={16} />
          Export
        </MenuItem>
      </Menu>
    </>
  );
}

function InsightChartMenuDeleteItem({
  action,
  onClose,
}: {
  action: NonNullable<InsightChartCardProps['removeAction']>;
  onClose: () => void;
}): JSX.Element {
  const { classes } = useStyles();
  const item = (
    <MenuItem
      dense
      disabled={Boolean(action.disabledReason)}
      onClick={() => {
        onClose();
        action.onRemove();
      }}
    >
      <Trash2 className={classes.menuIcon} size={16} />
      Delete
    </MenuItem>
  );
  if (!action.disabledReason) return item;

  return (
    <AlineTooltip arrow placement="left" title={action.disabledReason}>
      <span>{item}</span>
    </AlineTooltip>
  );
}

function InsightChartControls({
  dimensions,
  model,
  onPrimaryKeysChange,
  onSecondaryKeysChange,
  primaryKeys,
  secondaryKeys,
}: {
  dimensions: readonly string[];
  model: InsightChartModel;
  onPrimaryKeysChange: (keys: string[] | null) => void;
  onSecondaryKeysChange: (keys: string[] | null) => void;
  primaryKeys: string[] | null;
  secondaryKeys: string[] | null;
}): JSX.Element | null {
  const { classes } = useStyles();
  const namedAxisKeys = model.xKeys.filter(key => key !== WORKFLOW_CHART_HIDDEN_SERIES_KEY);
  const hasPrimaryOverflow =
    model.primaryCatalog.length > INSIGHT_CHART_CATEGORY_LIMIT &&
    namedAxisKeys.length <= INSIGHT_CHART_CATEGORY_LIMIT;
  const hasSecondaryOverflow = model.secondaryCatalog.length > INSIGHT_CHART_CATEGORY_LIMIT;
  if (!hasPrimaryOverflow && !hasSecondaryOverflow) return null;

  const primaryLabel = getDimensionLabel(dimensions.at(0));
  const secondaryLabel = getDimensionLabel(dimensions.at(1));

  return (
    <div className={classes.controls}>
      {hasPrimaryOverflow && (
        <InsightSeriesSelector
          catalog={model.primaryCatalog}
          label={primaryLabel}
          limit={INSIGHT_CHART_CATEGORY_LIMIT}
          manualKeys={primaryKeys}
          visibleKeys={namedAxisKeys}
          onKeysChange={onPrimaryKeysChange}
        />
      )}
      {hasSecondaryOverflow && (
        <InsightSeriesSelector
          catalog={model.secondaryCatalog}
          label={secondaryLabel}
          limit={INSIGHT_CHART_CATEGORY_LIMIT}
          manualKeys={secondaryKeys}
          visibleKeys={model.series.map(series => series.id)}
          onKeysChange={onSecondaryKeysChange}
        />
      )}
    </div>
  );
}

/** Unknown fields fall back to the neutral "Categories" rather than field jargon. */
function getDimensionLabel(dimension: string | undefined): string {
  if (!dimension || !getIsWorkflowChartDimension(dimension)) return 'Categories';
  return WORKFLOW_CHART_DIMENSION_PLURAL_LABELS[dimension];
}

/**
 * The server keeps the remainder series short enough for a legend row ("Other (15)"),
 * so its hover is what names the buckets it stands for.
 */
function getSeriesTooltip(options: {
  hiddenCount: number;
  model: InsightChartModel;
  series: InsightChartModel['series'][number];
  colorDimension?: string;
}): string {
  const { colorDimension, hiddenCount, model, series } = options;
  if (series.id !== WORKFLOW_CHART_HIDDEN_SERIES_KEY || hiddenCount <= 0) {
    return getInsightTooltipSeriesLabel(model, series.id, series.label) ?? series.label;
  }
  const dimensionLabel = getDimensionCountLabel(colorDimension, hiddenCount);
  return `${hiddenCount} additional ${dimensionLabel.toLocaleLowerCase()}`;
}

/** Dimension name agreeing in number with `count`; unknown fields read as categories. */
function getDimensionCountLabel(dimension: string | undefined, count: number): string {
  if (!dimension || !getIsWorkflowChartDimension(dimension)) {
    return count === 1 ? 'category' : 'categories';
  }
  if (count === 1) return WORKFLOW_CHART_DIMENSION_LABELS[dimension];
  return WORKFLOW_CHART_DIMENSION_PLURAL_LABELS[dimension];
}

/** Singular Dimension name for an axis title; unknown fields stay untitled. */
function getDimensionAxisLabel(dimension: string | undefined): string | undefined {
  if (!dimension || !getIsWorkflowChartDimension(dimension)) return undefined;
  return WORKFLOW_CHART_DIMENSION_LABELS[dimension];
}

function InsightSeriesSelector({
  catalog,
  label,
  limit,
  manualKeys,
  onKeysChange,
  visibleKeys,
}: {
  catalog: InsightChartCategory[];
  label: string;
  limit: number;
  manualKeys: string[] | null;
  onKeysChange: (keys: string[] | null) => void;
  visibleKeys: string[];
}): JSX.Element {
  const { classes } = useStyles();
  /**
   * Anchored to the footer row rather than the Customize link so the popover keeps
   * its position while choosing buckets rewords the summary and reveals Reset.
   */
  const footerRowRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const visibleKeySet = new Set(manualKeys ?? visibleKeys);
  const selected = catalog.filter(item => visibleKeySet.has(item.id));
  const filteredCatalog = catalog.filter(item =>
    item.label.toLocaleLowerCase().includes(search.toLocaleLowerCase())
  );
  const labelLower = label.toLocaleLowerCase();
  const summary = manualKeys
    ? `${selected.length} of ${catalog.length} ${labelLower} selected`
    : `Top ${selected.length} of ${catalog.length} ${labelLower}`;

  const handleClose = () => {
    setOpen(false);
    setSearch('');
  };

  const handleToggle = (item: InsightChartCategory) => {
    if (visibleKeySet.has(item.id)) {
      if (selected.length === 1) return;
      onKeysChange(
        selected.filter(selectedItem => selectedItem.id !== item.id).map(value => value.id)
      );
      return;
    }
    if (selected.length >= limit) return;
    onKeysChange([...selected.map(value => value.id), item.id]);
  };

  return (
    <>
      <div ref={footerRowRef} className={classes.footerRow}>
        <span>{summary}</span>
        <InsightSeriesFooterAction
          ariaLabel={`Choose ${labelLower}`}
          label="Customize"
          onClick={() => setOpen(true)}
        />
        {Boolean(manualKeys) && (
          <InsightSeriesFooterAction
            ariaLabel={`Reset ${labelLower}`}
            label="Reset"
            onClick={() => onKeysChange(null)}
          />
        )}
      </div>
      <Popover
        anchorEl={footerRowRef.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={open && Boolean(footerRowRef.current)}
        slotProps={{ paper: { className: classes.selectorPaper } }}
        onClose={handleClose}
      >
        <Box className={classes.selector}>
          <Box className={classes.selectorHeader}>
            <Typography className={classes.title} variant="subtitle2">
              Customize {labelLower}
            </Typography>
            <Typography color="textSecondary" variant="body2">
              Choose up to {limit} to show on this chart.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              className={classes.selectorSearch}
              placeholder={`Search ${labelLower}...`}
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} />
                    </InputAdornment>
                  ),
                },
              }}
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </Box>
          <Divider />
          <div className={classes.selectorOptions}>
            {filteredCatalog.map(item => {
              const checked = visibleKeySet.has(item.id);
              const disabled = !checked && selected.length >= limit;
              return (
                <ListItemButton
                  key={item.id}
                  aria-checked={checked}
                  className={classes.selectorOption}
                  disabled={disabled}
                  role="checkbox"
                  onClick={() => handleToggle(item)}
                >
                  <AlineCheckbox
                    disableRipple
                    checked={checked}
                    classes={{ root: classes.selectorOptionCheck }}
                    density="compact"
                    size="small"
                    slotProps={{ htmlInput: { 'aria-hidden': true, tabIndex: -1 } }}
                  />
                  <span className={classes.selectorOptionLabel} title={item.label}>
                    {item.label}
                  </span>
                  <span className={classes.selectorOptionCount}>
                    {item.count.toLocaleString()} {item.count === 1 ? 'workflow' : 'workflows'}
                  </span>
                </ListItemButton>
              );
            })}
            {filteredCatalog.length === 0 && (
              <Typography className={classes.selectorEmpty} variant="body2">
                No matching {labelLower}
              </Typography>
            )}
          </div>
          <Divider />
          <div className={classes.selectorFooter}>
            <Typography color="textSecondary" variant="caption">
              {selected.length} of {limit} selected
            </Typography>
            <Button size="small" startIcon={<Check size={16} />} onClick={handleClose}>
              Done
            </Button>
          </div>
        </Box>
      </Popover>
    </>
  );
}

/** Text action in a Chart footer, separated from the preceding text by a bullet. */
function InsightSeriesFooterAction({
  ariaLabel,
  label,
  onClick,
}: {
  ariaLabel: string;
  label: string;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}): JSX.Element {
  const { classes } = useStyles();

  return (
    <span data-hide-on-export>
      <span aria-hidden>·</span>
      <Link
        aria-label={ariaLabel}
        className={classes.footerAction}
        component="button"
        type="button"
        underline="hover"
        onClick={onClick}
      >
        {label}
      </Link>
    </span>
  );
}

function InsightChartLegend({
  colorDimension,
  model,
}: {
  model: InsightChartModel;
  colorDimension?: string;
}): JSX.Element | null {
  const { classes } = useStyles();
  const theme = useTheme();
  const palette = rainbowSurgePalette(theme.palette.mode);
  if (model.series.length < 2) return null;

  const namedSeriesCount = model.series.filter(
    series => series.id !== WORKFLOW_CHART_HIDDEN_SERIES_KEY
  ).length;
  const hiddenCount = model.secondaryCatalog.length - namedSeriesCount;

  return (
    <div className={classes.legend}>
      {model.series.map((series, index) => (
        <Tooltip
          key={series.id}
          title={getSeriesTooltip({ colorDimension, hiddenCount, model, series })}
        >
          <div className={classes.legendItem}>
            <span
              className={classes.legendSwatch}
              style={{
                backgroundColor: getSeriesColor(series.id, index, palette, colorDimension),
              }}
            />
            <span className={classes.legendLabel}>{series.label}</span>
          </div>
        </Tooltip>
      ))}
    </div>
  );
}

function InsightChartBody({
  apiRef,
  chartType,
  colorDimension,
  emptyState,
  error,
  loading,
  model,
  onSelectDataPoint,
  primaryLabel,
  rotateCategoryLabels,
  secondaryLabel,
  skipAnimation,
}: {
  apiRef: InsightChartApiRef;
  chartType: WorkflowChartType;
  error: boolean;
  loading: boolean;
  model: InsightChartModel | null;
  skipAnimation: boolean;
  colorDimension?: string;
  emptyState?: React.ReactNode;
  onSelectDataPoint?: (point: InsightChartDataPoint) => void;
  primaryLabel?: string;
  rotateCategoryLabels?: boolean;
  secondaryLabel?: string;
}): JSX.Element {
  const { classes } = useStyles();

  if (emptyState) {
    return <div className={classes.empty}>{emptyState}</div>;
  }

  if (loading && !model) {
    return (
      <Skeleton
        className={classes.chartSkeleton}
        height={INSIGHT_CHART_HEIGHT_PX}
        variant="rounded"
      />
    );
  }

  if (error) {
    return (
      <Typography className={classes.empty} color="error" variant="body2">
        Could not load chart series.
      </Typography>
    );
  }

  if (!model || model.xLabels.length === 0) {
    return (
      <Typography className={classes.empty} color="textSecondary" variant="body2">
        No workflows match this chart.
      </Typography>
    );
  }

  if (getIsMedianSuppressed(model)) {
    return (
      <Typography className={classes.empty} color="textSecondary" variant="body2">
        Not enough completed workflows in this window to show a typical time.
      </Typography>
    );
  }

  const Plot = INSIGHT_CHART_PLOTS[chartType];
  return (
    <InsightChartTooltipProvider drilldown={Boolean(onSelectDataPoint)} model={model}>
      <div className={onSelectDataPoint ? classes.interactiveChart : undefined}>
        <Plot
          apiRef={apiRef}
          colorDimension={colorDimension}
          model={model}
          primaryLabel={primaryLabel}
          rotateCategoryLabels={rotateCategoryLabels}
          secondaryLabel={secondaryLabel}
          skipAnimation={skipAnimation}
          onSelectDataPoint={onSelectDataPoint}
        />
      </div>
    </InsightChartTooltipProvider>
  );
}

function getValueDateDimensions(values: unknown): Partial<Record<string, WorkflowChartDimension>> {
  if (!Array.isArray(values)) return {};
  const mapped: Partial<Record<string, WorkflowChartDimension>> = {};
  for (const value of values) {
    const parsed = WorkflowChartSeriesValueSchema.safeParse(value);
    if (!parsed.success || !parsed.data.dateDimension) continue;
    mapped[parsed.data.id] = parsed.data.dateDimension;
  }
  return mapped;
}
